import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import { CopyableAddress, CopyableMint } from '../common/CopyButton';
import SafeBalanceDisplay from '../common/SafeBalanceDisplay';
import '../../styles/trading-modal.css';

const TradingModal = ({ isOpen, onClose, token, tradeType = 'buy' }) => {
  const { tokens, isAuthenticated } = useAuth();
  const { wallets, activeWallet } = useWallet();
  const { showSuccess, showError, showConfirmation } = useToast();
  const { isDark } = useTheme();

  // Modal state
  const [currentStep, setCurrentStep] = useState('configure'); // configure, preview, executing, success
  const [loading, setLoading] = useState(false);

  // Trade configuration
  const [tradeConfig, setTradeConfig] = useState({
    type: tradeType,
    walletId: '',
    amount: '',
    amountType: 'custom', // custom, percentage
    percentage: 50,
    slippageBps: 50, // 0.5%
    priorityFee: 5000,
    customSlippage: false
  });

  // Real-time data
  const [walletBalances, setWalletBalances] = useState({});
  const [balanceCache, setBalanceCache] = useState({});
  const [tokenInfo, setTokenInfo] = useState(null);
  const [priceQuote, setPriceQuote] = useState(null);
  const [tradingFees, setTradingFees] = useState(null);
  const [tradePreview, setTradePreview] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumpbackend.urion.live/api';

  // Get platform wallets for trading
  const platformWallets = wallets.filter(w => w.wallet_type === 'platform');
  const selectedWallet = platformWallets.find(w => w.id.toString() === tradeConfig.walletId);

  // Authenticated request helper
  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('Please log in to trade');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Initialize modal when opened
  useEffect(() => {
    if (isOpen && token) {
      setCurrentStep('configure');
      const selectedWalletId = activeWallet?.wallet_type === 'platform' ? activeWallet.id.toString() :
                              platformWallets.length > 0 ? platformWallets[0].id.toString() : '';

      setTradeConfig(prev => ({
        ...prev,
        type: tradeType,
        walletId: selectedWalletId
      }));

      fetchTokenInfo();
      if (selectedWalletId) {
        fetchWalletBalance(selectedWalletId);
      }
    }
  }, [isOpen, token, tradeType, activeWallet, platformWallets]);

  // Fetch balance when wallet selection changes
  useEffect(() => {
    if (tradeConfig.walletId && isAuthenticated) {
      fetchWalletBalance(tradeConfig.walletId);
    }
  }, [tradeConfig.walletId, isAuthenticated]);

  // Fetch token information
  const fetchTokenInfo = async () => {
    if (!token?.address) return;

    try {
      const response = await fetch(`${API_BASE_URL}/token-info/${token.address}`);
      const data = await response.json();

      if (data.success) {
        setTokenInfo(data.data.token);
      }
    } catch (error) {
      console.warn('Could not fetch token info:', error.message);
    }
  };

  // Fetch wallet balance for selected wallet only
  const fetchWalletBalance = async (walletId) => {
    if (!isAuthenticated || !walletId) return;

    // Check cache first
    const cacheKey = `wallet_${walletId}`;
    const now = Date.now();
    const cached = balanceCache[cacheKey];

    if (cached && (now - cached.timestamp) < 30000) { // 30 second cache
      setWalletBalances(prev => ({
        ...prev,
        [walletId]: cached.balance
      }));
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`/wallets/${walletId}/balance`);
      if (response.success) {
        const balance = response.data.balance;
        setWalletBalances(prev => ({
          ...prev,
          [walletId]: balance
        }));

        // Update cache
        setBalanceCache(prev => ({
          ...prev,
          [cacheKey]: {
            balance,
            timestamp: now
          }
        }));
      }
    } catch (error) {
      console.warn(`Could not fetch balance for wallet ${walletId}:`, error.message);
      setWalletBalances(prev => ({
        ...prev,
        [walletId]: { sol: 0 }
      }));
    }
  };

  // Calculate trade amount based on configuration
  const calculateTradeAmount = useCallback(() => {
    if (!selectedWallet || !walletBalances[selectedWallet.id]) return 0;

    const balance = walletBalances[selectedWallet.id];

    if (tradeConfig.amountType === 'percentage') {
      if (tradeConfig.type === 'buy') {
        // Calculate percentage of SOL balance (minus fees)
        const maxAmount = Math.max(0, balance.sol - 0.02);
        return (maxAmount * tradeConfig.percentage / 100);
      } else {
        // For sell, we'd need token balance - simplified for now
        return 0; // TODO: Implement token balance calculation
      }
    } else {
      return parseFloat(tradeConfig.amount) || 0;
    }
  }, [selectedWallet, walletBalances, tradeConfig]);

  // Get price quote and trade preview
  const updateTradePreview = useCallback(async () => {
    const amount = calculateTradeAmount();
    if (!amount || !selectedWallet || !token) return;

    try {
      setLoading(true);

      // Get price quote
      const quoteResponse = await fetch(`${API_BASE_URL}/trading/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inputMint: tradeConfig.type === 'buy' ? 'So11111111111111111111111111111111111111112' : token.address,
          outputMint: tradeConfig.type === 'buy' ? token.address : 'So11111111111111111111111111111111111111112',
          amount: tradeConfig.type === 'buy' ? Math.floor(amount * 1000000000) : Math.floor(amount * 1000000),
          slippageBps: tradeConfig.slippageBps
        })
      });

      const quoteData = await quoteResponse.json();
      if (quoteData.success) {
        setPriceQuote(quoteData.data.quote);
        
        // Calculate trading fees
        const feesResponse = await fetch(`${API_BASE_URL}/token-info/trading-fees`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            inputMint: tradeConfig.type === 'buy' ? 'So11111111111111111111111111111111111111112' : token.address,
            outputMint: tradeConfig.type === 'buy' ? token.address : 'So11111111111111111111111111111111111111112',
            amount: amount
          })
        });

        const feesData = await feesResponse.json();
        if (feesData.success) {
          setTradingFees(feesData.data.fees);
        }

        // Create trade preview
        setTradePreview({
          inputAmount: amount,
          outputAmount: tradeConfig.type === 'buy' ? 
            (quoteData.data.quote.outputAmount / 1000000) : 
            (quoteData.data.quote.outputAmount / 1000000000),
          priceImpact: quoteData.data.quote.priceImpact || 0,
          exchangeRate: quoteData.data.quote.exchangeRate || 0,
          minimumReceived: quoteData.data.quote.outputAmount * (1 - tradeConfig.slippageBps / 10000),
          route: quoteData.data.quote.route || []
        });
      }
    } catch (error) {
      console.warn('Could not get trade preview:', error.message);
    } finally {
      setLoading(false);
    }
  }, [calculateTradeAmount, selectedWallet, token, tradeConfig, API_BASE_URL]);

  // Update preview when configuration changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentStep === 'configure' && calculateTradeAmount() > 0) {
        updateTradePreview();
      }
    }, 500); // Debounce updates

    return () => clearTimeout(timeoutId);
  }, [updateTradePreview, currentStep, calculateTradeAmount]);

  // Execute trade
  const executeTrade = async () => {
    if (!selectedWallet || !tradePreview) return;

    setCurrentStep('executing');
    setLoading(true);

    try {
      const endpoint = tradeConfig.type === 'buy' ? '/trading/manual-buy' : '/trading/manual-sell';
      const amount = calculateTradeAmount();

      const result = await makeAuthenticatedRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          tokenMint: token.address,
          walletId: selectedWallet.id,
          [tradeConfig.type === 'buy' ? 'solAmount' : 'tokenAmount']: amount,
          slippageBps: tradeConfig.slippageBps,
          priorityFee: tradeConfig.priorityFee
        })
      });

      if (result.success) {
        setCurrentStep('success');
        showSuccess(`${tradeConfig.type === 'buy' ? 'Buy' : 'Sell'} order completed successfully!`);
        
        // Refresh balances for the selected wallet
        await fetchWalletBalance(selectedWallet.id);
      } else {
        throw new Error(result.error?.message || 'Trade failed');
      }
    } catch (error) {
      showError(`Trade failed: ${error.message}`);
      setCurrentStep('configure');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (currentStep === 'executing') return; // Prevent closing during execution
    
    setCurrentStep('configure');
    setTradeConfig(prev => ({ ...prev, amount: '', percentage: 50 }));
    setPriceQuote(null);
    setTradingFees(null);
    setTradePreview(null);
    onClose();
  };

  // Handle step navigation
  const goToPreview = () => {
    if (calculateTradeAmount() <= 0) {
      showError('Please enter a valid trade amount');
      return;
    }
    setCurrentStep('preview');
  };

  const goBackToConfig = () => {
    setCurrentStep('configure');
  };

  if (!isOpen) return null;

  return (
    <div className="trading-modal-overlay" onClick={handleClose}>
      <div className={`trading-modal ${isDark ? 'dark' : 'light'}`} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="trading-modal-header">
          <div className="modal-title">
            <span className={`trade-type-badge ${tradeConfig.type}`}>
              {tradeConfig.type === 'buy' ? '📈' : '📉'} {tradeConfig.type.toUpperCase()}
            </span>
            <div className="token-info">
              <img 
                src={token?.logo} 
                alt={token?.name}
                className="token-logo-small"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/24x24/6366f1/ffffff?text=${token?.symbol?.charAt(0) || '?'}`;
                }}
              />
              <span>{token?.symbol}</span>
            </div>
          </div>
          <button className="modal-close-btn" onClick={handleClose} disabled={currentStep === 'executing'}>
            ×
          </button>
        </div>

        {/* Step Indicator */}
        <div className="step-indicator" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '40px',
          // display:'none'
        }}>
          <div className={`step ${currentStep === 'configure' ? 'active' : currentStep !== 'configure' ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Configure</span>
          </div>
          <div className={`step ${currentStep === 'preview' ? 'active' : currentStep === 'executing' || currentStep === 'success' ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Preview</span>
          </div>
          <div className={`step ${currentStep === 'executing' ? 'active' : currentStep === 'success' ? 'completed' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Execute</span>
          </div>
        </div>

        {/* Modal Content */}
        <div className="trading-modal-content">
          {currentStep === 'configure' && (
            <ConfigureStep
              tradeConfig={tradeConfig}
              setTradeConfig={setTradeConfig}
              platformWallets={platformWallets}
              walletBalances={walletBalances}
              token={token}
              tokenInfo={tokenInfo}
              calculateTradeAmount={calculateTradeAmount}
              onNext={goToPreview}
              loading={loading}
            />
          )}

          {currentStep === 'preview' && (
            <PreviewStep
              tradeConfig={tradeConfig}
              selectedWallet={selectedWallet}
              token={token}
              tradePreview={tradePreview}
              tradingFees={tradingFees}
              calculateTradeAmount={calculateTradeAmount}
              onBack={goBackToConfig}
              onExecute={executeTrade}
              loading={loading}
            />
          )}

          {currentStep === 'executing' && (
            <ExecutingStep
              tradeConfig={tradeConfig}
              token={token}
              calculateTradeAmount={calculateTradeAmount}
            />
          )}

          {currentStep === 'success' && (
            <SuccessStep
              tradeConfig={tradeConfig}
              token={token}
              calculateTradeAmount={calculateTradeAmount}
              onClose={handleClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Configure Step Component
const ConfigureStep = ({ 
  tradeConfig, 
  setTradeConfig, 
  platformWallets, 
  walletBalances, 
  token, 
  tokenInfo,
  calculateTradeAmount,
  onNext,
  loading 
}) => {
  const { isDark } = useTheme();
  const selectedWallet = platformWallets.find(w => w.id.toString() === tradeConfig.walletId);
  const walletBalance = selectedWallet ? walletBalances[selectedWallet.id] : null;
  
  return (
    <div className="configure-step" >
      {/* Wallet Selection */}
      <div className={`form-section ${isDark ? 'dark' : 'light'}`}>
        <label className="form-label">Trading Wallet</label>
        <select
          className="form-select"
          value={tradeConfig.walletId}
          onChange={(e) => setTradeConfig(prev => ({ ...prev, walletId: e.target.value }))}
        >
          <option value="">Select wallet...</option>
          {platformWallets.map(wallet => (
            <option key={wallet.id} value={wallet.id}>
              {wallet.name || `Wallet ${wallet.id}`} - {walletBalances[wallet.id]?.sol?.toFixed(4) || '0'} SOL
            </option>
          ))}
        </select>
        
        {selectedWallet && (
          <div className="wallet-details">
            <CopyableAddress
              address={selectedWallet.wallet_address}
              truncate={true}
              truncateLength={8}
            />
            <SafeBalanceDisplay compact={true} walletId={selectedWallet?.id} />
          </div>
        )}
      </div>

      {/* Amount Configuration */}
      <div 
      // className='{form-section ${isDark ? 'dark' : 'light'} }` 
      className={`form-section ${isDark ? 'dark' : 'light'}`}
      
      >
        <label className="form-label">
          {tradeConfig.type === 'buy' ? 'SOL Amount' : 'Token Amount'}
        </label>
        
        <div className="amount-type-selector">
          <button
            className={`amount-type-btn ${tradeConfig.amountType === 'custom' ? 'active' : ''}`}
            onClick={() => setTradeConfig(prev => ({ ...prev, amountType: 'custom' }))}
          >
            Custom Amount
          </button>
          <button
            className={`amount-type-btn ${tradeConfig.amountType === 'percentage' ? 'active' : ''}`}
            onClick={() => setTradeConfig(prev => ({ ...prev, amountType: 'percentage' }))}
          >
            Percentage
          </button>
        </div>

        {tradeConfig.amountType === 'custom' ? (
          <div className="custom-amount-input">
            <input
              type="number"
              className="form-input"
              value={tradeConfig.amount}
              onChange={(e) => setTradeConfig(prev => ({ ...prev, amount: e.target.value }))}
              placeholder={`Enter ${tradeConfig.type === 'buy' ? 'SOL' : token?.symbol || 'token'} amount`}
              step="0.000001"
              min="0"
            />
            <span className="input-suffix">
              {tradeConfig.type === 'buy' ? 'SOL' : token?.symbol || 'TOKEN'}
            </span>
          </div>
        ) : (
          <div className="percentage-controls">
            <div className="percentage-slider">
              <input
                type="range"
                min="1"
                max="100"
                value={tradeConfig.percentage}
                onChange={(e) => setTradeConfig(prev => ({ ...prev, percentage: parseInt(e.target.value) }))}
                className="slider"
              />
              <div className="percentage-display">{tradeConfig.percentage}%</div>
            </div>
            <div className="percentage-buttons">
              {[25, 50, 75, 100].map(pct => (
                <button
                  key={pct}
                  className={`percentage-btn ${tradeConfig.percentage === pct ? 'active' : ''}`}
                  onClick={() => setTradeConfig(prev => ({ ...prev, percentage: pct }))}
                >
                  {pct === 100 ? 'MAX' : `${pct}%`}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="calculated-amount">
          <span className="amount-label">Trade Amount:</span>
          <span className="amount-value">
            {calculateTradeAmount().toFixed(6)} {tradeConfig.type === 'buy' ? 'SOL' : token?.symbol || 'TOKEN'}
          </span>
        </div>
      </div>

      {/* Advanced Options */}
      <div className={`form-section ${isDark ? 'dark' : 'light'}`}>
        <div className="section-header">
          <label className="form-label">Advanced Options</label>
        </div>
        
        <div className="advanced-options">
          <div className="option-row">
            <label className="option-label">Slippage Tolerance</label>
            <div className="slippage-controls">
              {!tradeConfig.customSlippage ? (
                <div className="slippage-presets">
                  {[10, 50, 100].map(bps => (
                    <button
                      key={bps}
                      className={`slippage-btn ${tradeConfig.slippageBps === bps ? 'active' : ''}`}
                      onClick={() => setTradeConfig(prev => ({ ...prev, slippageBps: bps }))}
                    >
                      {bps / 100}%
                    </button>
                  ))}
                  <button
                    className="slippage-btn custom"
                    onClick={() => setTradeConfig(prev => ({ ...prev, customSlippage: true }))}
                  >
                    Custom
                  </button>
                </div>
              ) : (
                <div className="custom-slippage">
                  <input
                    type="number"
                    className="slippage-input"
                    value={tradeConfig.slippageBps / 100}
                    onChange={(e) => setTradeConfig(prev => ({ 
                      ...prev, 
                      slippageBps: Math.max(1, Math.min(5000, parseFloat(e.target.value) * 100)) 
                    }))}
                    step="0.1"
                    min="0.1"
                    max="50"
                  />
                  <span className="input-suffix">%</span>
                  <button
                    className="reset-btn"
                    onClick={() => setTradeConfig(prev => ({ ...prev, customSlippage: false, slippageBps: 50 }))}
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="option-row">
            <label className="option-label">Priority Fee</label>
            <div className="priority-fee-controls">
              <select
                className="form-select small"
                value={tradeConfig.priorityFee}
                onChange={(e) => setTradeConfig(prev => ({ ...prev, priorityFee: parseInt(e.target.value) }))}
              >
                <option value={0}>None (Slow)</option>
                <option value={1000}>Low (0.001 SOL)</option>
                <option value={5000}>Medium (0.005 SOL)</option>
                <option value={10000}>High (0.01 SOL)</option>
                <option value={50000}>Turbo (0.05 SOL)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Token Information */}
      {tokenInfo && (
        <div 
        className={`form-section ${isDark ? 'dark' : 'light'}`}
        
        >
          <label className="form-label">Token Information</label>
          <div className="token-info-display">
            <div className="token-metrics">
              <div className="metric">
                <span className="metric-label">Price</span>
                <span className="metric-value">
                  ${tokenInfo.priceUSD ? (tokenInfo.priceUSD < 0.001 ? tokenInfo.priceUSD.toExponential(3) : tokenInfo.priceUSD.toFixed(6)) : 'N/A'}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Market Cap</span>
                <span className="metric-value">
                  ${tokenInfo.marketCap ? (tokenInfo.marketCap / 1000000).toFixed(2) + 'M' : 'N/A'}
                </span>
              </div>
              {tokenInfo.isVerified && (
                <div className="verified-badge">✓ Verified</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Next Button */}
      <div className="form-actions">
        <button
          className="btn btn-primary"
          onClick={onNext}
          disabled={!tradeConfig.walletId || calculateTradeAmount() <= 0 || loading}
        >
          {loading ? 'Loading...' : 'Review Trade'}
        </button>
      </div>
    </div>
  );
};

// Preview Step Component
const PreviewStep = ({ 
  tradeConfig, 
  selectedWallet, 
  token, 
  tradePreview, 
  tradingFees,
  calculateTradeAmount,
  onBack, 
  onExecute, 
  loading 
}) => {
  return (
    <div className="preview-step">
      <div className="trade-summary">
        <h3>Trade Summary</h3>
        
        <div className="summary-row">
          <span className="label">Action:</span>
          <span className="value">
            {tradeConfig.type === 'buy' ? 'Buy' : 'Sell'} {token?.symbol}
          </span>
        </div>

        <div className="summary-row">
          <span className="label">Wallet:</span>
          <span className="value">
            <CopyableAddress 
              address={selectedWallet?.wallet_address}
              truncate={true}
              truncateLength={8}
            />
          </span>
        </div>

        <div className="summary-row">
          <span className="label">
            {tradeConfig.type === 'buy' ? 'Spending:' : 'Selling:'}
          </span>
          <span className="value">
            {calculateTradeAmount().toFixed(6)} {tradeConfig.type === 'buy' ? 'SOL' : token?.symbol}
          </span>
        </div>

        {tradePreview && (
          <>
            <div className="summary-row">
              <span className="label">
                {tradeConfig.type === 'buy' ? 'Receiving:' : 'Getting:'}
              </span>
              <span className="value">
                {tradePreview.outputAmount.toFixed(6)} {tradeConfig.type === 'buy' ? token?.symbol : 'SOL'}
              </span>
            </div>

            <div className="summary-row">
              <span className="label">Exchange Rate:</span>
              <span className="value">
                1 {tradeConfig.type === 'buy' ? 'SOL' : token?.symbol} = {tradePreview.exchangeRate?.toFixed(6)} {tradeConfig.type === 'buy' ? token?.symbol : 'SOL'}
              </span>
            </div>

            <div className="summary-row">
              <span className="label">Price Impact:</span>
              <span className={`value ${tradePreview.priceImpact > 5 ? 'warning' : ''}`}>
                {tradePreview.priceImpact?.toFixed(2)}%
              </span>
            </div>

            <div className="summary-row">
              <span className="label">Minimum Received:</span>
              <span className="value">
                {(tradePreview.minimumReceived / (tradeConfig.type === 'buy' ? 1000000 : 1000000000)).toFixed(6)} {tradeConfig.type === 'buy' ? token?.symbol : 'SOL'}
              </span>
            </div>
          </>
        )}

        <div className="summary-row">
          <span className="label">Slippage Tolerance:</span>
          <span className="value">{(tradeConfig.slippageBps / 100).toFixed(1)}%</span>
        </div>

        {tradingFees && (
          <div className="summary-row">
            <span className="label">Estimated Fees:</span>
            <span className="value">{tradingFees.totalFee?.toFixed(6)} SOL</span>
          </div>
        )}
      </div>

      {tradePreview?.priceImpact > 5 && (
        <div className="warning-box">
          <div className="warning-icon">⚠️</div>
          <div className="warning-text">
            <strong>High Price Impact Warning</strong>
            <p>This trade will have a {tradePreview.priceImpact.toFixed(2)}% price impact. Consider reducing the trade size.</p>
          </div>
        </div>
      )}

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onBack} disabled={loading}>
          Back
        </button>
        <button 
          className="btn btn-primary"
          onClick={onExecute}
          disabled={loading || !tradePreview}
        >
          {loading ? 'Loading...' : `Execute ${tradeConfig.type === 'buy' ? 'Buy' : 'Sell'}`}
        </button>
      </div>
    </div>
  );
};

// Executing Step Component
const ExecutingStep = ({ tradeConfig, token, calculateTradeAmount }) => {
  return (
    <div className="executing-step">
      <div className="executing-animation">
        <div className="spinner"></div>
        <div className="executing-icon">⚡</div>
      </div>
      <h3>Executing Trade...</h3>
      <p>
        {tradeConfig.type === 'buy' ? 'Buying' : 'Selling'} {calculateTradeAmount().toFixed(6)} {tradeConfig.type === 'buy' ? 'SOL worth of' : ''} {token?.symbol}
      </p>
      <div className="progress-steps">
        <div className="step completed">✓ Validating trade parameters</div>
        <div className="step completed">✓ Getting price quote</div>
        <div className="step active">⏳ Executing on blockchain</div>
        <div className="step">⏳ Confirming transaction</div>
      </div>
    </div>
  );
};

// Success Step Component
const SuccessStep = ({ tradeConfig, token, calculateTradeAmount, onClose }) => {
  return (
    <div className="success-step">
      <div className="success-animation">
        <div className="success-icon">✅</div>
      </div>
      <h3>Trade Successful!</h3>
      <p>
        Successfully {tradeConfig.type === 'buy' ? 'bought' : 'sold'} {calculateTradeAmount().toFixed(6)} {tradeConfig.type === 'buy' ? 'SOL worth of' : ''} {token?.symbol}
      </p>
      
      <div className="success-actions">
        <button className="btn btn-primary" onClick={onClose}>
          Close
        </button>
        <button className="btn btn-secondary" onClick={() => window.open(`https://solscan.io/`, '_blank')}>
          View on Solscan
        </button>
      </div>
    </div>
  );
};

export default TradingModal;